import type { AppMeta, AuthUser } from "@shared/types";
import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { api, unwrap } from "@/lib/api";
import { authClient } from "@/lib/auth-client";

export const sessionKey = ["session"] as const;
export const metaKey = ["meta"] as const;

// biome-ignore lint/suspicious/noExplicitAny: the auth client's user type carries extra fields
export function toAuthUser(u: any): AuthUser {
  return {
    id: u.id,
    name: u.name,
    email: u.email,
    image: u.image ?? null,
    role: u.role === "admin" ? "admin" : "member",
  };
}

async function fetchSession(): Promise<AuthUser | null> {
  const { data } = await authClient.getSession();
  return data?.user ? toAuthUser(data.user) : null;
}

/** The signed-in user (or null). Loaded once by the root route, refreshed after sign-in/out. */
export const sessionQuery = () => ({
  queryKey: sessionKey,
  queryFn: fetchSession,
  staleTime: 60_000,
});

export const metaQuery = () => ({
  queryKey: metaKey,
  queryFn: () => unwrap<AppMeta>(api.meta.$get()),
  staleTime: 60_000,
});

export function useSessionUser(): AuthUser | null {
  return useQuery(sessionQuery()).data ?? null;
}

/** After a successful sign-in: seed the cache and let the router re-run its guards. */
export async function applySignedInUser(qc: QueryClient, user: AuthUser) {
  qc.setQueryData(sessionKey, user);
  await qc.invalidateQueries({ queryKey: metaKey });
}

export function useSignOut() {
  const qc = useQueryClient();
  const router = useRouter();
  return async () => {
    await authClient.signOut();
    qc.setQueryData(sessionKey, null);
    qc.removeQueries({ queryKey: ["roadmaps"] });
    qc.removeQueries({ queryKey: ["roadmap"] });
    await router.invalidate();
    await router.navigate({ to: "/login" });
  };
}

/** Only relative paths are honoured as post-login destinations (no open redirects). */
export function safeRedirect(target: string | undefined): string {
  return target?.startsWith("/") && !target.startsWith("//") ? target : "/";
}
